import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import './ToolPage.css'
import './NumberDrawer.css'
import { drawUniqueNumbers } from './utils/numberDrawer'
import { trackEvent } from '../../lib/analytics'
import { useDocumentMeta } from '../../hooks/useDocumentMeta'

const NumberDrawer = () => {
  useDocumentMeta({
    title: 'Sorteador de Números | jarder.dev',
    description:
      'Digite um intervalo, veja a contagem regressiva e descubra o ' +
      'número sorteado. Grátis, direto no navegador.',
  })

  const [min, setMin] = useState('1')
  const [max, setMax] = useState('100')
  const [quantity, setQuantity] = useState('1')
  const [status, setStatus] = useState('idle')
  const [display, setDisplay] = useState(null)
  const [result, setResult] = useState(null)
  const [error, setError] = useState('')
  const [history, setHistory] = useState([])
  const intervalRef = useRef(null)
  const timeoutRef = useRef(null)

  useEffect(
    () => () => {
      clearInterval(intervalRef.current)
      clearTimeout(timeoutRef.current)
    },
    [],
  )

  const draw = () => {
    const minNum = Number.parseInt(min, 10)
    const maxNum = Number.parseInt(max, 10)
    const quantityNum = Number.parseInt(quantity, 10) || 1

    if (Number.isNaN(minNum) || Number.isNaN(maxNum) || minNum >= maxNum) {
      setError('O valor mínimo precisa ser menor que o máximo.')
      return
    }
    const rangeSize = maxNum - minNum + 1
    if (quantityNum < 1 || quantityNum > rangeSize) {
      setError(`Escolha uma quantidade entre 1 e ${rangeSize}.`)
      return
    }

    setError('')
    setResult(null)
    setStatus('drawing')

    intervalRef.current = setInterval(() => {
      setDisplay(Math.floor(Math.random() * (maxNum - minNum + 1)) + minNum)
    }, 70)

    timeoutRef.current = setTimeout(() => {
      clearInterval(intervalRef.current)
      const finalNumbers = drawUniqueNumbers(minNum, maxNum, quantityNum)
      setResult(finalNumbers)
      setStatus('revealed')
      setHistory((current) => [finalNumbers, ...current].slice(0, 8))
      trackEvent('tool_generate', { tool: 'number_drawer', quantity: quantityNum })
    }, 1800)
  }

  return (
    <div className="tool-page">
      <Link to="/ferramentas" className="tool-back-link">
        ← Todas as ferramentas
      </Link>
      <h1>Sorteador de Números</h1>
      <p className="tool-disclaimer">
        Escolha o intervalo e a quantidade de números, o sorteio roda
        localmente no seu navegador.
      </p>

      <div className="tool-row">
        <div className="tool-field">
          <label htmlFor="min">Mínimo</label>
          <input
            id="min"
            type="number"
            value={min}
            onChange={(e) => setMin(e.target.value)}
          />
        </div>
        <div className="tool-field">
          <label htmlFor="max">Máximo</label>
          <input
            id="max"
            type="number"
            value={max}
            onChange={(e) => setMax(e.target.value)}
          />
        </div>
        <div className="tool-field">
          <label htmlFor="quantity">Quantidade</label>
          <input
            id="quantity"
            type="number"
            min="1"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
          />
        </div>
      </div>

      {error && <p className="tool-error">{error}</p>}

      <button
        type="button"
        className="tool-button"
        onClick={draw}
        disabled={status === 'drawing'}
      >
        {status === 'drawing' ? 'Sorteando...' : 'Sortear'}
      </button>

      {status === 'drawing' && (
        <div className="number-drawer-display drawing">{display}</div>
      )}

      {status === 'revealed' && result && (
        <div className="number-drawer-display revealed">
          {result.map((num) => (
            <span key={num} className="number-drawer-ball">
              {num}
            </span>
          ))}
        </div>
      )}

      {history.length > 0 && (
        <div style={{ marginTop: 28 }}>
          <span className="tool-import-hint">Sorteios anteriores</span>
          <ul className="tool-list">
            {history.map((numbers, i) => (
              <li key={i}>{numbers.join(', ')}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="tool-info-section">
        <h2>Ideias de uso</h2>
        <p>
          Defina o intervalo mínimo e máximo e quantos números sortear de
          uma vez, sem repetição dentro do mesmo sorteio. Útil para
          rifas, sortear a ordem de apresentações, escolher um número da
          sorte ou qualquer decisão que precise de um número aleatório
          dentro de uma faixa específica. O histórico dos últimos
          sorteios fica visível na página, caso precise conferir depois.
        </p>
      </div>
    </div>
  )
}

export default NumberDrawer
